/**
 * Conversational form mode (F6)
 *
 * An alternative rendering of the public form: instead of a page of controls,
 * the respondent is asked for one field at a time and answers in their own
 * words. Each answer is sent to `conversation.answerTurn`, which reads it into
 * the value that field actually wants and hands it straight back.
 *
 * Three rules this component keeps:
 *
 * 1. It stores nothing of its own. Every answer goes into the ordinary form
 *    state through `setFieldValue`, and the submission runs through the same
 *    `submitForm` as a form filled by hand, so validation, CAPTCHA, payment and
 *    integrations behave identically.
 * 2. Nobody is ever trapped. "Switch to standard form" is on screen the whole
 *    time, a field that will not parse falls back to its own control after two
 *    clarifying questions, and a field type that cannot be described in a
 *    sentence (a file, a signature, a payment, a CAPTCHA) gets its real control
 *    from the start.
 * 3. It says what it is. The disclosure sits above the transcript, not in a
 *    toast that disappears.
 */

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {Bot, CheckCircle2, Loader2, MessageSquare, Send, SlidersHorizontal, User} from 'lucide-react';
import {Alert, AlertDescription, AlertTitle, Button, Textarea, cn, useLanguage} from '@shondalai/ui-commons';
import {useFormSubmission} from '../../contexts/form-submission-context';
import {conversationService} from '../../services/conversation.service';
import {aiErrorCode} from '../../services/ai-features.service';
import {FormFieldRenderer} from './form-field-renderer';
import type {FormField} from '../../types/form-builder.types';

type ChatRole = 'assistant' | 'respondent' | 'note';

interface ChatMessage {
  id: string;
  role: ChatRole;
  text: string;
  /** Choice labels shown as hints under an assistant question. */
  choices?: string[];
}

export interface ConversationalFormProps {
  /** Leave the chat and render the ordinary form instead. */
  onSwitchToStandard: () => void;
}

let messageCounter = 0;

function nextMessageId(): string {
  messageCounter += 1;

  return `ef-chat-${messageCounter}`;
}

function fieldNameOf(field: FormField): string {
  return field.config.fieldName || field.id;
}

export function ConversationalForm({onSwitchToStandard}: ConversationalFormProps) {
  const {t} = useLanguage();
  const {
    form,
    pages,
    errors,
    isSubmitting,
    setFieldValue,
    validateField,
    validatePage,
    submitForm,
  } = useFormSubmission();

  const formId = form?.id ?? 0;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [answered, setAnswered] = useState<Set<string>>(() => new Set());
  const [input, setInput] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [manual, setManual] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [assistantDone, setAssistantDone] = useState(false);
  const [turnsLeft, setTurnsLeft] = useState<number | null>(null);

  const askedRef = useRef<string | null>(null);
  const transcriptRef = useRef<HTMLDivElement | null>(null);

  const session = useQuery({
    queryKey: ['easyforms', 'conversation', formId],
    queryFn: () => conversationService.start(formId),
    enabled: formId > 0,
    retry: 0,
    staleTime: Infinity,
  });

  const chatTypes = useMemo(
    () => new Set(session.data?.chat_types ?? []),
    [session.data?.chat_types],
  );
  const skipTypes = useMemo(
    () => new Set(session.data?.skip_types ?? []),
    [session.data?.skip_types],
  );
  const maxRetries = session.data?.max_retries ?? 2;

  // Every field the respondent still has to answer, in page order. This is
  // derived from `pages`, which the context recomputes whenever an answer
  // changes what conditional logic shows, so a rule that reveals a field mid
  // conversation simply adds a question.
  const steps = useMemo(() => {
    const out: FormField[] = [];

    pages.forEach((page) => {
      page.fields.forEach((field) => {
        if (!skipTypes.has(field.type)) {
          out.push(field);
        }
      });
    });

    return out;
  }, [pages, skipTypes]);

  const currentStep = useMemo(
    () => steps.find((field) => !answered.has(field.id)) ?? null,
    [steps, answered],
  );

  const push = useCallback((role: ChatRole, text: string, choices?: string[]) => {
    if (!text) return;

    setMessages((prev) => [...prev, {id: nextMessageId(), role, text, choices}]);
  }, []);

  const questionFor = useCallback(
    (field: FormField): {text: string; choices?: string[]} => {
      const label = field.config.label || fieldNameOf(field);
      const parts = [label];

      if (field.config.helpText) {
        parts.push(field.config.helpText);
      }

      if (!field.config.required) {
        parts.push(t('COM_EASYFORMS_AI_CONVERSATION_OPTIONAL', 'This one is optional.'));
      }

      const choices = (field.config.options || [])
        .map((option) => option.label || String(option.value ?? ''))
        .filter(Boolean)
        .slice(0, 12);

      return {text: parts.join('\n'), choices: choices.length ? choices : undefined};
    },
    [t],
  );

  // Opening line, once the server has said the conversation may happen. This
  // effect is declared before the one that asks the first question, because
  // effects run in declaration order and the greeting has to be first in the
  // transcript.
  useEffect(() => {
    if (!session.data) return;

    const greeting = session.data.greeting;

    setTurnsLeft(session.data.turns_left);
    setMessages((prev) => (prev.length > 0 ? prev : [
      {id: nextMessageId(), role: 'assistant', text: greeting},
    ]));
  }, [session.data]);

  // Ask for the current field exactly once, and reset the per-field retry
  // counter when the conversation moves on.
  useEffect(() => {
    if (!session.data || !currentStep) return;
    if (askedRef.current === currentStep.id) return;

    askedRef.current = currentStep.id;
    setAttempt(0);
    setManual(assistantDone || !chatTypes.has(currentStep.type));

    const question = questionFor(currentStep);
    push('assistant', question.text, question.choices);
  }, [session.data, currentStep, chatTypes, assistantDone, questionFor, push]);

  useEffect(() => {
    transcriptRef.current?.scrollTo({top: transcriptRef.current.scrollHeight, behavior: 'smooth'});
  }, [messages, isThinking]);

  const markAnswered = useCallback((fieldId: string) => {
    setAnswered((prev) => {
      const next = new Set(prev);
      next.add(fieldId);

      return next;
    });
  }, []);

  const stopAssisting = useCallback((reason: string) => {
    setAssistantDone(true);
    setManual(true);
    push('note', reason);
  }, [push]);

  const send = useCallback(async () => {
    const text = input.trim();

    if (!text || !currentStep || isThinking) return;

    push('respondent', text);
    setInput('');
    setIsThinking(true);

    try {
      const turn = await conversationService.answer(formId, fieldNameOf(currentStep), text);

      setTurnsLeft(turn.turns_left);

      if (turn.valid) {
        setFieldValue(currentStep.id, turn.value);
        markAnswered(currentStep.id);

        if (turn.display) {
          push('note', t('COM_EASYFORMS_AI_CONVERSATION_RECORDED', 'Recorded') + ': ' + turn.display);
        }

        return;
      }

      const used = attempt + 1;
      setAttempt(used);
      push(
        'assistant',
        turn.clarify
        || t('COM_EASYFORMS_AI_CONVERSATION_RETRY_GENERIC', 'Sorry, I did not follow that. Could you say it another way?'),
      );

      if (used >= maxRetries) {
        setManual(true);
        push(
          'note',
          t(
            'COM_EASYFORMS_AI_CONVERSATION_FALLBACK',
            'Let us do this one the usual way instead.',
          ),
        );
      }
    } catch (error) {
      const code = aiErrorCode(error);
      const message = error instanceof Error && error.message
        ? error.message
        : t('COM_EASYFORMS_AI_CONVERSATION_UNAVAILABLE', 'The assistant is unavailable right now.');

      if (code === 'ai_conversation_limit' || code === 'ai_feature_disabled' || code === 'ai_not_connected') {
        stopAssisting(message);

        return;
      }

      push('assistant', message);
      setManual(true);
    } finally {
      setIsThinking(false);
    }
  }, [
    input, currentStep, isThinking, formId, attempt, maxRetries,
    push, setFieldValue, markAnswered, stopAssisting, t,
  ]);

  const confirmManual = useCallback(async () => {
    if (!currentStep) return;

    const valid = await validateField(currentStep.id);

    if (!valid) return;

    markAnswered(currentStep.id);
  }, [currentStep, validateField, markAnswered]);

  const skipStep = useCallback(() => {
    if (!currentStep) return;

    markAnswered(currentStep.id);
  }, [currentStep, markAnswered]);

  const finish = useCallback(async () => {
    const results = await Promise.all(pages.map((_page, index) => validatePage(index)));

    if (!results.every(Boolean)) {
      push(
        'note',
        t(
          'COM_EASYFORMS_AI_CONVERSATION_NEEDS_FIXING',
          'A few answers still need attention. Opening the standard form so you can see them.',
        ),
      );
      onSwitchToStandard();

      return;
    }

    await submitForm();
  }, [pages, validatePage, submitForm, onSwitchToStandard, push, t]);

  const switchLink = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onSwitchToStandard}
      className="text-xs"
    >
      <SlidersHorizontal className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
      {t('COM_EASYFORMS_AI_CONVERSATION_SWITCH', 'Switch to standard form')}
    </Button>
  );

  if (session.isLoading) {
    return (
      <div className="flex items-center justify-between gap-3 py-6">
        <span className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          {t('COM_EASYFORMS_AI_CONVERSATION_STARTING', 'Starting the conversation...')}
        </span>
        {switchLink}
      </div>
    );
  }

  if (session.isError || !session.data) {
    const message = session.error instanceof Error && session.error.message
      ? session.error.message
      : t('COM_EASYFORMS_AI_CONVERSATION_UNAVAILABLE', 'The assistant is unavailable right now.');

    return (
      <div className="space-y-4 py-2">
        <Alert className="border-border/40 bg-muted/30">
          <MessageSquare className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
          <AlertTitle className="text-sm font-medium">
            {t('COM_EASYFORMS_AI_CONVERSATION_NOT_AVAILABLE', 'Chat is not available')}
          </AlertTitle>
          <AlertDescription className="mt-1.5 text-sm">{message}</AlertDescription>
        </Alert>
        <Button type="button" onClick={onSwitchToStandard}>
          {t('COM_EASYFORMS_AI_CONVERSATION_CONTINUE_STANDARD', 'Continue with the standard form')}
        </Button>
      </div>
    );
  }

  const answeredCount = steps.filter((field) => answered.has(field.id)).length;
  const complete = currentStep === null && steps.length > 0;
  const currentError = currentStep ? errors[currentStep.id] : undefined;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/40 pb-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Bot className="h-4 w-4 text-indigo-500" aria-hidden="true" />
          {form?.title}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {t('COM_EASYFORMS_AI_CONVERSATION_PROGRESS', 'Answered')}
            {': '}
            {answeredCount}
            {' / '}
            {steps.length}
          </span>
          {switchLink}
        </div>
      </div>

      <p className="text-xs text-muted-foreground">{session.data.disclosure}</p>

      <div
        ref={transcriptRef}
        className="max-h-[26rem] space-y-3 overflow-y-auto pr-1"
        role="log"
        aria-live="polite"
      >
        {messages.map((message) => {
          if (message.role === 'note') {
            return (
              <p key={message.id} className="flex items-start gap-2 text-xs text-muted-foreground">
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span>{message.text}</span>
              </p>
            );
          }

          const isRespondent = message.role === 'respondent';

          return (
            <div
              key={message.id}
              className={cn('flex items-start gap-2', isRespondent && 'flex-row-reverse')}
            >
              <span
                className={cn(
                  'mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
                  isRespondent ? 'bg-muted' : 'bg-indigo-500/10',
                )}
              >
                {isRespondent
                  ? <User className="h-3.5 w-3.5" aria-hidden="true" />
                  : <Bot className="h-3.5 w-3.5 text-indigo-500" aria-hidden="true" />}
              </span>
              <div
                className={cn(
                  'max-w-[85%] rounded-lg px-3 py-2 text-sm',
                  isRespondent ? 'bg-muted' : 'border border-border/40 bg-card',
                )}
              >
                <span className="whitespace-pre-line">{message.text}</span>
                {message.choices && (
                  <ul className="mt-2 flex flex-wrap gap-1.5">
                    {message.choices.map((choice) => (
                      <li
                        key={choice}
                        className="rounded border border-border/40 px-2 py-0.5 text-xs text-muted-foreground"
                      >
                        {choice}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          );
        })}

        {isThinking && (
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            {t('COM_EASYFORMS_AI_CONVERSATION_THINKING', 'Reading your answer...')}
          </p>
        )}
      </div>

      {currentStep && manual && (
        <div className="space-y-3 rounded-lg border border-border/40 bg-card p-4">
          <FormFieldRenderer field={currentStep} />
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" onClick={confirmManual}>
              {t('COM_EASYFORMS_AI_CONVERSATION_CONFIRM', 'Continue')}
            </Button>
            {!currentStep.config.required && (
              <Button type="button" size="sm" variant="ghost" onClick={skipStep}>
                {t('COM_EASYFORMS_AI_CONVERSATION_SKIP', 'Skip this one')}
              </Button>
            )}
          </div>
        </div>
      )}

      {currentStep && !manual && (
        <div className="space-y-2">
          <label htmlFor="ef-conversation-input" className="sr-only">
            {t('COM_EASYFORMS_AI_CONVERSATION_INPUT_LABEL', 'Your answer')}
          </label>
          <Textarea
            id="ef-conversation-input"
            rows={2}
            value={input}
            disabled={isThinking}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                void send();
              }
            }}
            placeholder={t('COM_EASYFORMS_AI_CONVERSATION_PLACEHOLDER', 'Type your answer')}
          />
          {currentError && (
            <p className="text-xs text-destructive">{currentError.message}</p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" size="sm" onClick={() => void send()} disabled={isThinking || !input.trim()}>
              <Send className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
              {t('COM_EASYFORMS_AI_CONVERSATION_SEND', 'Send')}
            </Button>
            {!currentStep.config.required && (
              <Button type="button" size="sm" variant="ghost" onClick={skipStep} disabled={isThinking}>
                {t('COM_EASYFORMS_AI_CONVERSATION_SKIP', 'Skip this one')}
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setManual(true)}
              disabled={isThinking}
            >
              {t('COM_EASYFORMS_AI_CONVERSATION_USE_CONTROL', 'Answer with the normal control')}
            </Button>
            {turnsLeft !== null && turnsLeft <= 3 && (
              <span className="text-xs text-muted-foreground">
                {t('COM_EASYFORMS_AI_CONVERSATION_TURNS_LEFT', 'Assistant replies left')}
                {': '}
                {turnsLeft}
              </span>
            )}
          </div>
        </div>
      )}

      {complete && (
        <div className="space-y-3 rounded-lg border border-border/40 bg-card p-4">
          <p className="text-sm">
            {t('COM_EASYFORMS_AI_CONVERSATION_READY', 'That is everything. Ready to send it?')}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={() => void finish()} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
              {t('COM_EASYFORMS_FORM_SUBMIT', 'Submit')}
            </Button>
            <Button type="button" variant="outline" onClick={onSwitchToStandard}>
              {t('COM_EASYFORMS_AI_CONVERSATION_REVIEW', 'Review my answers first')}
            </Button>
          </div>
        </div>
      )}

      {steps.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {t('COM_EASYFORMS_AI_CONVERSATION_NOTHING_TO_ASK', 'There is nothing to fill in on this form.')}
        </p>
      )}
    </div>
  );
}

export default ConversationalForm;
